import React, { useMemo } from 'react';
import { Alert, Typography } from '@mui/material';
import Button from '../ActionButton';
import { dateLabel, parseDay, shiftDay, ymd } from './dashboardModel';

/** Reusable, controlled order-volume calendar for a bounded date window. */
export default function CalendarPanel({
  today,
  earliest,
  month,
  onMonthChange,
  selectedDay,
  onSelectDay,
  onClearSelection,
  rows = [],
  loading = false,
  error = '',
  onRetry,
}) {
  const cells = useMemo(() => {
    const first = `${month}-01`;
    const offset = parseDay(first).getDay();
    return Array.from({ length: 42 }, (_, index) => shiftDay(first, index - offset));
  }, [month]);
  const counts = useMemo(
    () => new Map(rows.map(row => [String(row.date).slice(0, 10), Number(row.total_orders) || 0])),
    [rows],
  );
  const monthMax = useMemo(
    () => Math.max(0, ...cells.filter(date => date.slice(0, 7) === month).map(date => counts.get(date) || 0)),
    [cells, counts, month],
  );
  const heatLevel = date => {
    const count = counts.get(date) || 0;
    if (!count || !monthMax) return 0;
    return Math.max(1, Math.min(4, Math.ceil((count / monthMax) * 4)));
  };
  const changeMonth = offset => {
    const date = parseDay(`${month}-01`);
    date.setMonth(date.getMonth() + offset);
    onMonthChange(ymd(date).slice(0, 7));
  };

  return <section className="dashboard-surface dashboard-calendar" aria-label="Select dashboard date">
    <div className="dashboard-calendar-heading">
      <Button variant="text" aria-label="Previous month" onClick={() => changeMonth(-1)} disabled={month <= earliest.slice(0, 7)}>‹</Button>
      <Typography variant="subtitle1">{parseDay(`${month}-01`).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</Typography>
      <Button variant="text" aria-label="Next month" onClick={() => changeMonth(1)} disabled={month >= today.slice(0, 7)}>›</Button>
    </div>
    <div className="dashboard-calendar-grid">
      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => <span key={day} className="dashboard-weekday">{day}</span>)}
      {cells.map(date => <button key={date} type="button" disabled={date < earliest || date > today || date.slice(0, 7) !== month}
        data-heat={heatLevel(date)} title={`${counts.get(date) || 0} orders`}
        aria-label={dateLabel(date)} aria-pressed={selectedDay === date} aria-current={date === today ? 'date' : undefined}
        onClick={() => onSelectDay(date)}>{parseDay(date).getDate()}</button>)}
    </div>
    {error && <Alert severity="error">Calendar heatmap unavailable. {error} <Button variant="text" onClick={onRetry}>Retry</Button></Alert>}
    <div className="dashboard-calendar-footer">
      {loading ? <Typography role="status" variant="caption" color="text.secondary">Loading calendar activity…</Typography> :
        <div className="dashboard-calendar-legend" aria-label="Order volume heatmap legend">
          <Typography component="span" variant="caption" color="text.secondary">Fewer orders</Typography>
          {[1, 2, 3, 4].map(level => <span key={level} data-heat={level} aria-hidden="true" />)}
          <Typography component="span" variant="caption" color="text.secondary">More orders</Typography>
        </div>}
      <Button variant="text" size="small" onClick={onClearSelection} disabled={!selectedDay}>Last 30 days</Button>
    </div>
  </section>;
}
